import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { ImportType } from "./import.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Bind } from "../../decorators/bind.decorator";
import { RowData } from "../s25-spreadsheet/types/sheet.js.types";
import { CustomAttribute, EventI, EventProfile } from "../../pojo/EventI";
import { jSith } from "../../util/jquery-replacement";
import { ContactService } from "../../services/contact.service";
import { SpaceService } from "../../services/space.service";
import { Conflicts, EventImportSaveResponseDto, ImportService } from "./import.service";
import { NameService } from "../../services/name.service";
import { Item } from "../../pojo/Item";
import { EventService } from "../../services/event.service";
import { S25Util } from "../../util/s25-util";
import { UserprefService } from "../../services/userpref.service";
import { EventTypeService } from "../../services/master.definitions/event.misc/event.type.service";
import { OrganizationService } from "../../services/organization.service";
import { EventCategoryService } from "../../services/master.definitions/categories/event.category.service";
import { CustomAttributeEventService } from "../../services/master.definitions/custom.attributes/custom.attribute.event.service";
import { EventSpreadsheetUtil } from "./util/event/event.spreadsheet.util";
import { ImportUtil } from "./util/import.util";
import { EventImportUtil } from "./util/event/event.import.util";
import { ResourceService } from "../../services/resource.service";
import { ScrollLockService } from "../../services/scroll.lock.service";

@Component({
    selector: "s25-ng-import-events",
    template: `
        @if (init) {
            <div class="c-margin-bottom--single">
                Select a fallback folder for unrouted events. Or select an override folder to force all events to be
                imported into that folder.
            </div>

            @if (!this.overrideFolder) {
                <div class="c-margin-bottom--single">
                    <span>Fallback Folder&nbsp;</span>
                    <s25-ng-dropdown-search-criteria
                        class="ngInlineBlock"
                        [type]="'eventFolders'"
                        [(chosen)]="this.fallbackFolder"
                        [placeholder]="'Select a fallback folder'"
                    ></s25-ng-dropdown-search-criteria>
                    <button
                        class="c-margin-left--half aw-button aw-button--outline"
                        (click)="this.fallbackFolder = null"
                    >
                        Clear
                    </button>
                </div>
            }

            @if (!this.fallbackFolder) {
                <div class="c-margin-bottom--single">
                    <span>Override Folder&nbsp;</span>
                    <s25-ng-dropdown-search-criteria
                        class="ngInlineBlock"
                        [type]="'eventFolders'"
                        [(chosen)]="this.overrideFolder"
                        [placeholder]="'Select an override folder'"
                    ></s25-ng-dropdown-search-criteria>
                    <button
                        class="c-margin-left--half aw-button aw-button--outline"
                        (click)="this.overrideFolder = null"
                    >
                        Clear
                    </button>
                </div>
            }
        }
    `,
    imports: [S25DropdownPaginatedModule],
    standalone: true,
})
export class S25ImportEventsComponent implements OnInit {
    @Input() type: ImportType;
    @Input() parentCd?: ChangeDetectorRef;

    constructor(private cd: ChangeDetectorRef) {}

    init = false;
    fallbackFolder: DropDownItem = null;
    overrideFolder: DropDownItem = null;
    timeFormat: string;
    dateTimeFormat: string;
    eventTypeNameMap = new Map<string, number>();
    orgNameMap = new Map<string, number>();
    catNameMap = new Map<string, number>();
    custAttrNameMap = new Map<string, CustomAttribute>();

    @Bind
    async onCompleteEvents(rows: RowData[]) {
        try {
            this.type.errors = [];
            this.type.conflicts = null;
            this.type.successMessage = "";

            if (!rows || !rows.length) {
                this.type.errors.push("No sheet data found");
                this.refresh();
                return;
            }
            let extUidToEvent = new Map<string, EventI>();
            let extUidToProfile = new Map<string, EventProfile>();
            let rowsProcessedDefer = jSith.defer();
            let contNameMapDefer = jSith.defer();
            let locNameMapDefer = jSith.defer();
            let resNameMapDefer = jSith.defer();
            let contEmailSet = new Set<string>();
            let locNameSet = new Set<string>();
            let resNameSet = new Set<string>();
            let errorSet = new Set<string>();
            let postProcessPromises: Promise<unknown>[] = [];

            let events = EventSpreadsheetUtil.rowsToEvents(
                extUidToEvent,
                extUidToProfile,
                contEmailSet,
                locNameSet,
                resNameSet,
                errorSet,
                rows,
                rowsProcessedDefer.promise,
                this.eventTypeNameMap,
                this.orgNameMap,
                this.catNameMap,
                this.custAttrNameMap,
                contNameMapDefer.promise,
                locNameMapDefer.promise,
                resNameMapDefer.promise,
                postProcessPromises,
            );

            events = events.filter((e) => !!e);
            if (!events || !events.length) {
                this.type.errors.push("No valid events found");
                this.refresh();
                return;
            }

            // wait for all rows to be processed, which means contEmailSet and locNameSet are all filled and ready
            rowsProcessedDefer.resolve();

            // get locations and contacts by filtered names (emails) and form maps from name -> id
            // vvv filtered name -> id maps vvv
            let contNameMapPromise = ContactService.getContactsByEmails(Array.from(contEmailSet)).then((contacts) => {
                return contNameMapDefer.resolve(
                    contacts.reduce((map, cont) => {
                        map.set(cont.email, cont.contId);
                        return map;
                    }, new Map<string, number>()),
                );
            });

            let locNameMapPromise = SpaceService.getSpacesByName(Array.from(locNameSet)).then((locations) => {
                return locNameMapDefer.resolve(
                    locations.reduce((map, loc) => {
                        map.set(loc.roomShort, loc.roomId);
                        return map;
                    }, new Map<string, number>()),
                );
            });

            let resNameMapPromise = ResourceService.getResourcesByName(Array.from(resNameSet)).then((resources) => {
                return resNameMapDefer.resolve(
                    resources.reduce((map, res) => {
                        map.set(res.resourceName, res.resourceId);
                        return map;
                    }, new Map<string, number>()),
                );
            });
            // ^^^ filtered name -> id maps ^^^

            // wait for them to finish
            await contNameMapPromise;
            await locNameMapPromise;
            await resNameMapPromise;

            // wait for each row to process the new filtered name -> id maps
            await Promise.all(postProcessPromises);

            if (errorSet.size) {
                // show any errors found during processing, like a missing name -> id mapping
                this.type.errors = Array.from(errorSet);
                this.refresh();
                return;
            }

            EventImportUtil.addOccurrences(events, errorSet);
            if (errorSet.size) {
                // show any errors found during processing, like a missing name -> id mapping
                this.type.errors = Array.from(errorSet);
                this.refresh();
                return;
            }

            EventImportUtil.bindProfiles(events);
            EventImportUtil.relateEvents(events);

            this.type.errors = EventImportUtil.validateEvents(events);
            if (this.type.errors.length) {
                this.refresh();
                return;
            }

            let response = await this.importEvents(events);
            if (response?.error) {
                if (response.error === "no_events") {
                    this.type.errors.push("No events to import");
                } else if (response.error === "inheritance_failed") {
                    this.type.errors.push("Folder inheritance failed");
                } else if (response.error === "conflict") {
                    this.type.errors.push("Conflict(s) detected");
                    this.processConflicts(response.reservationSaveResponse?.conflicts);
                } else if (response.error === "fallback_folder_required") {
                    this.type.errors.push("Fallback folder is required for unrouted events");
                } else if (response.error === "no_reservations") {
                    this.type.errors.push("No reservations found for events");
                } else if (response.error === "sp_bulk_temp_table_creation_failed") {
                    this.type.errors.push("Failed to create temporary table for space reservations");
                } else if (response.error === "rs_bulk_temp_table_creation_failed") {
                    this.type.errors.push("Failed to create temporary table for resource reservations");
                } else {
                    this.type.errors.push("Unknown error: [" + response.error + "]");
                }
            } else if (!this.type.errors.length) {
                this.type.successMessage = "Events imported successfully";
            }

            this.refresh();
        } catch (ex) {
            console.error("Error importing events", ex);
            this.type.errors.push("Error importing events: [" + ex.message + "]");
            this.refresh();
        }
    }

    async processConflicts(conflicts: Conflicts) {
        if (!conflicts) {
            return;
        }
        let eventsToFetchNames = new Set<number>();
        let roomsToFetchNames = new Set<number>();
        let resourcesToFetchNames = new Set<number>();
        conflicts.spConflicts = conflicts.spConflicts || [];
        // sort conflicts by roomId and then by candidateRsrvStartDt
        conflicts.spConflicts.sort((a, b) => {
            if (a.roomId !== b.roomId) {
                return a.roomId - b.roomId;
            }
            return new Date(a.candidateRsrvStartDt).getTime() - new Date(b.candidateRsrvStartDt).getTime();
        });
        conflicts.spConflicts.forEach((spc) => {
            roomsToFetchNames.add(spc.roomId);
            spc.candidateEventId && eventsToFetchNames.add(spc.candidateEventId);
            if (spc.conflictEventId && ["rsrv", "pending"].includes(spc.conflictType)) {
                // note: blackouts use conflictEventId as the blackout id
                eventsToFetchNames.add(spc.conflictEventId);
            }
            spc.conflictTypeName = EventImportUtil.getConflictTypeName(spc.conflictType, true);
            spc.candidateDateTimeStr = ImportUtil.getDateTimeStr(
                spc.candidateRsrvStartDt,
                spc.candidateRsrvEndDt,
                this.timeFormat,
                this.dateTimeFormat,
            );
            spc.conflictDateTimeStr = ImportUtil.getDateTimeStr(
                spc.conflictRsrvStartDt,
                spc.conflictRsrvEndDt,
                this.timeFormat,
                this.dateTimeFormat,
            );
        });
        conflicts.rsConflicts = conflicts.rsConflicts || [];
        // sort conflicts by resourceId and then by candidateRsrvStartDt
        conflicts.rsConflicts.sort((a, b) => {
            if (a.resourceId !== b.resourceId) {
                return a.resourceId - b.resourceId;
            }
            return new Date(a.candidateRsrvStartDt).getTime() - new Date(b.candidateRsrvStartDt).getTime();
        });
        conflicts.rsConflicts.forEach((rsc) => {
            resourcesToFetchNames.add(rsc.resourceId);
            rsc.candidateEventId && eventsToFetchNames.add(rsc.candidateEventId);
            rsc.conflictEventId && eventsToFetchNames.add(rsc.conflictEventId);
            rsc.conflictTypeName = EventImportUtil.getConflictTypeName(rsc.conflictType, false);
            rsc.candidateDateTimeStr = ImportUtil.getDateTimeStr(
                rsc.candidateRsrvStartDt,
                rsc.candidateRsrvEndDt,
                this.timeFormat,
                this.dateTimeFormat,
            );
            rsc.conflictDateTimeStr = ImportUtil.getDateTimeStr(
                rsc.conflictRsrvStartDt,
                rsc.conflictRsrvEndDt,
                this.timeFormat,
                this.dateTimeFormat,
            );
        });

        let allNames = await NameService.getNames(
            [].concat(
                Array.from(eventsToFetchNames).map((eventId) => {
                    return { itemId: eventId, itemTypeId: Item.Ids.Event };
                }),
                Array.from(roomsToFetchNames).map((roomId) => {
                    return { itemId: roomId, itemTypeId: Item.Ids.Location };
                }),
                Array.from(resourcesToFetchNames).map((resourceId) => {
                    return { itemId: resourceId, itemTypeId: Item.Ids.Resource };
                }),
            ),
        );

        (conflicts.spConflicts || []).forEach((spc) => {
            spc.roomName = allNames[NameService.itemKeyFromIds(spc.roomId, Item.Ids.Location)];
            spc.candidateEventName = allNames[NameService.itemKeyFromIds(spc.candidateEventId, Item.Ids.Event)];
            spc.conflictEventName = allNames[NameService.itemKeyFromIds(spc.conflictEventId, Item.Ids.Event)];
        });

        (conflicts.rsConflicts || []).forEach((rsc) => {
            rsc.resourceName = allNames[NameService.itemKeyFromIds(rsc.resourceId, Item.Ids.Resource)];
            rsc.candidateEventName = allNames[NameService.itemKeyFromIds(rsc.candidateEventId, Item.Ids.Event)];
            rsc.conflictEventName = allNames[NameService.itemKeyFromIds(rsc.conflictEventId, Item.Ids.Event)];
        });

        this.type.conflicts = conflicts;
        this.refresh();
    }

    async importEvents(events: EventI[]): Promise<EventImportSaveResponseDto> {
        this.type.errors = [];
        if (!this.fallbackFolder?.itemId && !this.overrideFolder?.itemId) {
            this.type.errors.push("Please select a fallback or override folder");
            this.refresh();
            return;
        }
        if (!events || !events.length) {
            this.type.errors.push("No valid events found");
            this.refresh();
            return;
        }

        let fallbackCabinetId = null;
        let overrideCabinetId = null;
        if (this.overrideFolder?.itemId) {
            this.fallbackFolder = null;
        }
        if (this.fallbackFolder?.itemId) {
            this.overrideFolder = null;
            let fallbackFolder = await EventService.getFolderById(this.fallbackFolder?.itemId as number);
            if (fallbackFolder) {
                fallbackCabinetId = fallbackFolder.parent_id;
            }
        }
        if (this.overrideFolder?.itemId) {
            let overrideFolder = await EventService.getFolderById(this.overrideFolder.itemId as number);
            if (overrideFolder) {
                overrideCabinetId = overrideFolder.parent_id;
            }
        }

        return ImportService.importEvents({
            events: events,
            fallbackFolderId: S25Util.parseInt(this.fallbackFolder?.itemId),
            fallbackCabinetId: fallbackCabinetId,
            overrideFolderId: S25Util.parseInt(this.overrideFolder?.itemId) || null,
            overrideCabinetId: overrideCabinetId,
            allowShared: true,
            allowBlackoutOverride: true,
            allowClosedHoursOverride: true,
        });
    }

    async ngOnInit() {
        this.type.onComplete = this.onCompleteEvents;

        this.timeFormat = await UserprefService.getS25Timeformat();
        this.dateTimeFormat = await UserprefService.getS25DateTimeformat();

        // vvv unfiltered name -> id maps vvv
        this.eventTypeNameMap = await EventTypeService.getSingleton()
            .get()
            .then((eventTypes) => {
                return eventTypes.reduce((map, et) => {
                    map.set(et.type_name, et.type_id);
                    return map;
                }, new Map<string, number>());
            });

        this.orgNameMap = await OrganizationService.getAllOrganizationsList().then((orgs) => {
            return orgs.reduce((map, org) => {
                map.set(org.name, org.id);
                return map;
            }, new Map<string, number>());
        });

        this.catNameMap = await EventCategoryService.getSingleton()
            .get()
            .then((categories) => {
                return categories.reduce((map, cat) => {
                    map.set(cat.category_name, cat.category_id);
                    return map;
                }, new Map<string, number>());
            });

        this.custAttrNameMap = await CustomAttributeEventService.getSingleton()
            .get()
            .then((custAttrs) => {
                return custAttrs.reduce((map, ca) => {
                    map.set(ca.attribute_name, ca);
                    return map;
                }, new Map<string, CustomAttribute>());
            });
        // ^^^ unfiltered name -> id maps ^^^

        this.init = true;
        this.refresh();
    }

    refresh() {
        this.cd.detectChanges();
        this.parentCd?.detectChanges();
        ScrollLockService.unlockBodyScroll();
    }
}
